import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  CssBaseline,
  Fade,
  Link,
  InputAdornment,
  IconButton,
} from "@mui/material";

import { HourglassEmpty, Block } from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { getApiUrl } from "../utils/api";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { AuthContext } from "../utils/AuthContext";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(15px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export default function Login() {
  const { login } = useContext(AuthContext);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null); // 'pending' | 'rejected' | null

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (e) => e.preventDefault();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setAccountStatus(null);
    try {
      const response = await fetch(getApiUrl("/auth/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      // Handle pending / rejected status responses
      if (response.status === 403) {
        const msg = data.error || "";
        if (msg.toLowerCase().includes("pending") || msg.toLowerCase().includes("awaiting")) {
          setAccountStatus("pending");
        } else if (msg.toLowerCase().includes("rejected") || msg.toLowerCase().includes("not approved")) {
          setAccountStatus("rejected");
        } else {
          setError(msg);
        }
        return;
      }

      if (!response.ok) {
        setError(data.error || "Login failed");
        return;
      }

      // ── Store token + EFFECTIVE permissions via AuthContext ────────────
      // The login response already includes the correct, role-merged
      // permissions (data.permissions). Use them directly — do NOT re-fetch
      // from /auth/admin/... which is admin-only and 403s for normal roles.
      login(data.token, data.permissions || {});
      // ───────────────────────────────────────────────────────────────────

      const role = data.user?.role;

      setSuccess("Login successful! Redirecting...");

      setTimeout(() => {
        // Forced first-login password change takes priority
        if (data.mustChangePassword) {
          window.location.href = "/change-password";
          return;
        }

        switch (role) {
          case "Super admin":
          case "Admin":
          case "bio IT":
          case "finance":
            window.location.href = "/dashboard";
            break;
          case "sales":
            window.location.href = "/sample-registration";
            break;
          case "lab":
            window.location.href = "/sample-preparation";
            break;
          case "bio info":
            window.location.href = "/bio-informatics-data-analysis";
            break;
          case "Custom":
            window.location.href = "/dashboard";
            break;
          default:
            window.location.href = "/dashboard";
        }
      }, 1000);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `url('/BG.png')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0, left: 0,
          width: "100%", height: "100%",
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(2px)",
          zIndex: 1,
        }}
      />

      <Container component="main" maxWidth="xs" sx={{ zIndex: 2 }}>
        <CssBaseline />
        <Paper
          elevation={6}
          sx={{
            mt: 4, p: 4,
            borderRadius: 5,
            backdropFilter: "blur(14px)",
            background: "rgba(255, 255, 255, 0.85)",
            border: "1px solid rgba(255,255,255,0.3)",
            animation: `${fadeIn} 0.8s ease-out`,
          }}
        >
          <Fade in timeout={800}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>

              <Box
                component="img"
                src="/logo.png"
                alt="App Logo"
                sx={{
                  height: 70, width: 150,
                  borderRadius: "8px",
                  objectFit: "contain",
                  mb: 2,
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  backgroundColor: "#fff",
                  p: 0.5,
                }}
              />

              <Typography
                variant="subtitle1"
                sx={{ mb: 3, color: "#0D47A1", fontSize: "1.1rem", fontWeight: 500, textAlign: "center" }}
              >
                Unlocking the future of genomics
              </Typography>

              {/* PENDING STATUS BANNER */}
              {accountStatus === "pending" && (
                <Alert
                  severity="warning"
                  icon={<HourglassEmpty fontSize="inherit" />}
                  sx={{ width: "100%", mb: 2 }}
                >
                  <strong>Account Pending Approval</strong>
                  <br />
                  Your account is awaiting admin approval. You'll receive an email once it's approved.
                </Alert>
              )}

              {/* REJECTED STATUS BANNER */}
              {accountStatus === "rejected" && (
                <Alert
                  severity="error"
                  icon={<Block fontSize="inherit" />}
                  sx={{ width: "100%", mb: 2 }}
                >
                  <strong>Account Not Approved</strong>
                  <br />
                  Your registration was not approved. Please contact your administrator.
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ width: "100%", mb: 2 }}>
                  {success}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: "100%" }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      "&:hover fieldset": { borderColor: "#1976D2" },
                    },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      "&:hover fieldset": { borderColor: "#1976D2" },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3, mb: 2, py: 1.5,
                    borderRadius: 3,
                    fontWeight: "bold",
                    background: "linear-gradient(45deg, #2196F3 30%, #1976D2 90%)",
                    boxShadow: "0 6px 16px rgba(25,118,210,0.4)",
                    "&:hover": {
                      background: "linear-gradient(45deg, #1976D2 30%, #0D47A1 90%)",
                      boxShadow: "0 8px 20px rgba(25,118,210,0.6)",
                    },
                  }}
                >
                  Sign In
                </Button>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mt: 1 }}>
                <Link href="/forgot-password" variant="body2" sx={{ color: "#1976D2" }}>
                  Forgot password?
                </Link>
                <Link href="/create-account" variant="body2" sx={{ color: "#1976D2" }}>
                  Create account
                </Link>
              </Box>

            </Box>
          </Fade>
        </Paper>

        <Box sx={{ mt: 3, textAlign: "center", color: "#fff", zIndex: 2 }}>
          <Typography variant="body2">
            © {new Date().getFullYear()} All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}